<template>
	<div>
		<div class="gh-layout">
			<aside class="gh-intro">
				<h2 class="gh-intro__title">Template Fonts</h2>
				<p class="gh-intro__desc">
					Fonts for the text on postcard templates (Campaigns and Auto Campaigns → Template). Upload the
					<strong>.ttf</strong> files of a family here and it shows up in the <strong>Font Family</strong> menu of the template
					editor. Postcards are printed with these same files.
				</p>
				<p class="gh-intro__desc">
					Upload one file per weight (Light, Regular, Bold…). When a template asks for a weight the family doesn't have, the
					closest one is used.
				</p>
			</aside>

			<section class="gh-accounts">
				<div v-if="notice" class="alert mb-3" :class="notice.ok ? 'alert-success' : 'alert-danger'" role="alert">
					<button type="button" class="btn-close float-end" aria-label="Close" @click="notice = null"></button>
					{{ notice.text }}
				</div>

				<!-- Upload -->
				<div class="ada-card mb-3">
					<div class="gh-section__title"><i class="fas fa-upload me-1"></i> Upload font files</div>

					<div class="gh-field">
						<label for="tfFiles">TrueType files (.ttf), one or more of the same family</label>
						<input id="tfFiles" ref="fileInput" type="file" class="form-control" accept=".ttf" multiple @change="pickFiles" />
					</div>

					<template v-if="pending.length">
						<div class="gh-field">
							<label for="tfFamily">Family</label>
							<input id="tfFamily" class="form-control" v-model.trim="family" list="tfFamilies" autocomplete="off" placeholder="Aller" />
							<datalist id="tfFamilies">
								<option v-for="font in fonts" :key="font.value" :value="font.value">{{ font.label }}</option>
							</datalist>
							<div class="gh-help">
								Letters and digits only, no spaces (shown as "{{ familyLabel || '…' }}"). Pick an existing family to add or replace its files.
							</div>
						</div>

						<table class="table table-sm align-middle tf-table mb-3">
							<thead>
								<tr>
									<th>File</th>
									<th style="width: 170px">Weight</th>
									<th style="width: 80px">Italic</th>
									<th style="width: 40px"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(item, index) in pending" :key="item.key">
									<td>
										<div class="text-truncate tf-file-name" :title="item.file.name">{{ item.file.name }}</div>
										<div v-if="replaces(item)" class="gh-help">Replaces {{ replaces(item).name }}</div>
										<div v-if="item.error" class="text-danger small">{{ item.error }}</div>
									</td>
									<td>
										<select class="form-select form-select-sm" v-model.number="item.weight" :aria-label="`Weight of ${item.file.name}`">
											<option v-for="(weight, style) in weights" :key="style" :value="weight">{{ weight }} · {{ style }}</option>
										</select>
									</td>
									<td>
										<input class="form-check-input" type="checkbox" v-model="item.italic" :aria-label="`${item.file.name} is italic`" />
									</td>
									<td class="text-end">
										<button type="button" class="gh-icon-btn" :aria-label="`Remove ${item.file.name}`" @click="pending.splice(index, 1)">
											<i class="fas fa-times"></i>
										</button>
									</td>
								</tr>
							</tbody>
						</table>

						<div v-if="duplicateStyles" class="text-danger small mb-2">Two files have the same weight and italic setting.</div>

						<button type="button" class="gh-save-btn gh-save-btn--sm" :disabled="uploading || !canUpload" @click="upload">
							<i class="fas" :class="uploading ? 'fa-spinner fa-spin' : 'fa-upload'"></i>
							Upload {{ pending.length }} {{ pending.length === 1 ? 'file' : 'files' }}
						</button>
					</template>
				</div>

				<!-- Families -->
				<div v-if="loading" class="ada-card text-muted"><i class="fas fa-spinner fa-spin me-1"></i> Loading…</div>

				<div v-for="font in fonts" v-else :key="font.value" class="ada-card mb-3">
					<div class="d-flex align-items-center justify-content-between mb-2">
						<div class="gh-section__title mb-0">
							<i class="fas fa-font me-1"></i> {{ font.label }}
							<span v-if="font.is_default" class="badge bg-light text-dark ms-1">Default</span>
						</div>
						<code class="small text-muted">{{ font.value }}</code>
					</div>

					<div class="tf-sample" :style="{ fontFamily: previewFamily(font.value) }">
						<span v-for="file in uprightFiles(font)" :key="file.name" :style="{ fontWeight: file.weight }">City of Sample </span>
					</div>

					<table class="table table-sm align-middle tf-table mb-0">
						<tbody>
							<tr v-for="file in font.files" :key="file.name">
								<td :style="{ fontFamily: previewFamily(font.value), fontWeight: file.weight, fontStyle: file.italic ? 'italic' : 'normal' }">
									{{ file.weight }} · {{ file.style }}
								</td>
								<td class="text-muted small">{{ file.name }}</td>
								<td class="text-muted small text-end">{{ kb(file.size) }}</td>
								<td class="text-end" style="width: 40px">
									<button
										v-if="!(font.is_default && file.style === 'Regular')"
										type="button"
										class="gh-icon-btn"
										:aria-label="`Delete ${file.name}`"
										@click="askDelete(font, file)"
									>
										<i class="fas fa-trash"></i>
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</section>
		</div>

		<ConfirmModal
			ref="deleteModal"
			title="Delete font file?"
			confirm-text="Delete"
			confirm-icon="fa-trash"
			icon-class="fa-trash"
			danger
			:busy="deleting"
			@confirm="remove"
		>
			<template v-if="toDelete">
				<strong>{{ toDelete.file.name }}</strong> will be removed. Templates that use {{ toDelete.font.label }}
				{{ toDelete.file.style }} switch to the closest weight left<template v-if="toDelete.font.files.length === 1">, or to Plus Jakarta Sans when the family has no files left</template>.
			</template>
		</ConfirmModal>
	</div>
</template>

<script>
import restApi from '@/api/index'
import ConfirmModal from '@/components/common/ConfirmModal.vue'
import { loadTemplateFonts, previewFontFamily } from '@/utils/postcardPatterns'

const WEIGHTS = { Thin: 100, ExtraLight: 200, Light: 300, Regular: 400, Medium: 500, SemiBold: 600, Bold: 700, ExtraBold: 800, Black: 900 }

/** Guess weight from a file name like "Aller_Std_BdIt.ttf" or "OpenSans-SemiBold.ttf". Order matters: ExtraBold before Bold. */
const WEIGHT_HINTS = [
	[/thin|hairline/, 100],
	[/extra-?light|ultra-?light|xlight/, 200],
	[/semi-?bold|demi-?bold|(^|[^a-z])sb([^a-z]|$)/, 600],
	[/extra-?bold|ultra-?bold|xbold|heavy/, 800],
	[/black/, 900],
	[/bold|(^|[^a-z])bd/, 700],
	[/medium|(^|[^a-z])md/, 500],
	[/light|(^|[^a-z])lt/, 300],
]

function guessStyle(fileName) {
	const name = fileName.replace(/\.ttf$/i, '').toLowerCase()
	const hint = WEIGHT_HINTS.find(([pattern]) => pattern.test(name))
	const italic = /italic|oblique/.test(name) || /(bd|lt|rg|md|sb)?it$/.test(name.split(/[-_ ]/).pop())
	return { weight: hint ? hint[1] : 400, italic }
}

/** "Aller_Std_Bd.ttf" → "Aller", "open-sans-bold.ttf" → "Open". */
function guessFamily(fileName) {
	const first = fileName.replace(/\.ttf$/i, '').split(/[-_ ]/)[0] || ''
	const clean = first.replace(/[^A-Za-z0-9]/g, '')
	return clean ? clean[0].toUpperCase() + clean.slice(1) : ''
}

export default {
	name: 'TemplateFontsSettings',
	components: { ConfirmModal },
	data() {
		return {
			loading: true,
			fonts: [],
			weights: WEIGHTS,
			family: '',
			pending: [],
			uploading: false,
			deleting: false,
			toDelete: null,
			notice: null,
		}
	},
	computed: {
		familyValid() {
			return /^[A-Za-z][A-Za-z0-9]{0,49}$/.test(this.family)
		},
		familyLabel() {
			return this.family.replace(/(?<=[a-z0-9])(?=[A-Z])/g, ' ')
		},
		duplicateStyles() {
			const keys = this.pending.map(item => `${item.weight}${item.italic ? 'i' : ''}`)
			return new Set(keys).size !== keys.length
		},
		canUpload() {
			return this.pending.length && this.familyValid && !this.duplicateStyles
		},
	},
	created() {
		this.load()
	},
	methods: {
		async load(force = false) {
			this.loading = !this.fonts.length
			try {
				this.fonts = await loadTemplateFonts(force)
			} finally {
				this.loading = false
			}
		},
		previewFamily(family) {
			return previewFontFamily(family)
		},
		uprightFiles(font) {
			const upright = font.files.filter(file => !file.italic)
			return upright.length ? upright : font.files
		},
		kb(bytes) {
			return `${Math.round((bytes || 0) / 1024)} KB`
		},
		pickFiles(event) {
			const files = Array.from(event.target.files || [])
			this.pending = files.map((file, index) => ({ key: `${file.name}-${index}`, file, error: '', ...guessStyle(file.name) }))
			if (files.length && !this.family) this.family = guessFamily(files[0].name)
		},
		replaces(item) {
			const font = this.fonts.find(f => f.value === this.family)
			return font && font.files.find(file => file.weight === item.weight && file.italic === item.italic)
		},
		async upload() {
			this.uploading = true
			this.notice = null
			let uploaded = 0
			for (const item of [...this.pending]) {
				const body = new FormData()
				body.append('family', this.family)
				body.append('weight', item.weight)
				body.append('italic', item.italic ? '1' : '0')
				body.append('file', item.file)
				try {
					await restApi.post('/template-fonts', body)
					this.pending.splice(this.pending.indexOf(item), 1)
					uploaded++
				} catch (error) {
					item.error = this.errorText(error)
				}
			}
			await this.load(true)
			this.uploading = false

			if (!this.pending.length) {
				this.$refs.fileInput.value = ''
				this.notice = { ok: true, text: `Uploaded ${uploaded} ${uploaded === 1 ? 'file' : 'files'} to ${this.familyLabel}. It's in the template Font Family menu now.` }
				this.family = ''
			} else {
				this.notice = { ok: false, text: `${this.pending.length} of ${this.pending.length + uploaded} files didn't upload, see the errors below.` }
			}
		},
		askDelete(font, file) {
			this.toDelete = { font, file }
			this.$refs.deleteModal.open()
		},
		async remove() {
			const { font, file } = this.toDelete
			this.deleting = true
			try {
				await restApi.delete(`/template-fonts/${encodeURIComponent(font.value)}/${encodeURIComponent(file.name)}`)
				this.$refs.deleteModal.close()
				this.notice = { ok: true, text: `Deleted ${file.name}.` }
				await this.load(true)
			} catch (error) {
				this.$refs.deleteModal.close()
				this.notice = { ok: false, text: this.errorText(error) }
			} finally {
				this.deleting = false
			}
		},
		errorText(error) {
			const data = (error.response && error.response.data) || {}
			const first = data.error && typeof data.error === 'object' ? Object.values(data.error)[0] : null
			if (error.response && error.response.status === 413) return 'File is too large for the server.'
			return (first && first[0]) || data.message || 'Something went wrong.'
		},
	},
}
</script>

<style scoped lang="scss">
.tf-table {
	font-size: 13px;
	th {
		font-weight: 500;
		color: #8a909c;
		background: none;
		border-bottom: 1px solid #f1f3f5;
	}
	td {
		border-bottom: 1px solid #f1f3f5;
	}
	tr:last-child td {
		border-bottom: 0;
	}
}
.tf-file-name {
	max-width: 260px;
}
.tf-sample {
	font-size: 1.35rem;
	line-height: 1.4;
	margin-bottom: 0.5rem;
	color: #2b303b;
}
</style>
